import React, { useState, useEffect } from 'react';
import { FileText, RefreshCw, ExternalLink, Upload, Download, Edit, Trash, Plus, Save } from 'lucide-react';
import { Button } from '../common/Button';
import { ResumePreviewModal } from '../resume/ResumePreviewModal';
import { ResumeUploadModal } from '../resume/ResumeUploadModal';
import { AutocompleteChipField } from '../common/AutocompleteChipField';
import { FreeTextChipField } from '../common/FreeTextChipField';
import { useToast } from '../common/Toast';
import configService, { JobRole, ExperienceLevel, Language, InterviewGoal } from '../../services/configService';
import careerService from '../../services/careerService';
import LoadingIndicator from '../common/LoadingIndicator';
import { ResumeProtocol } from '../../types/resume';

// Import only UserPreferences interface since we're now importing ResumeProtocol
interface UserPreferences {
  jobRoles: string[];
  experienceLevel: string;
  languages: string[];
  interviewGoals: string[];
}

export const PreferenceSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [resumes, setResumes] = useState<ResumeProtocol[]>([]);
  const [preferences, setPreferences] = useState<UserPreferences>({
    jobRoles: [],
    experienceLevel: '',
    languages: [],
    interviewGoals: []
  });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedResume, setSelectedResume] = useState<ResumeProtocol | null>(null);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { showToast } = useToast();
  
  // State for config data
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevel[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [interviewGoals, setInterviewGoals] = useState<InterviewGoal[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | undefined>(undefined);
  
  // Handle responsive layout
  useEffect(() => {
    const checkMobileView = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileView(isMobile);
    };

    // Initialize on mount with a small delay to ensure correct measurement
    checkMobileView();
    
    // Set up ResizeObserver for more reliable detection
    const resizeObserver = new ResizeObserver(() => {
      // We only care about the window size changes
      checkMobileView();
    });
    
    // Observe the document body for size changes
    resizeObserver.observe(document.body);
    
    // Also listen for window resize events as a fallback
    window.addEventListener('resize', checkMobileView);
    
    // Force a re-check after a short delay to handle some edge cases with dev tools
    setTimeout(checkMobileView, 300);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
      resizeObserver.disconnect();
    };
  }, []);

  // Hide validation error when form becomes valid
  useEffect(() => {
    if (isFormValid() && showValidationError) {
      setShowValidationError(false);
    }
  }, [preferences]);
  
  // Fetch configuration data from API
  useEffect(() => {
    const fetchConfigData = async () => {
      setIsLoadingConfig(true);
      setConfigError(undefined);
      
      try {
        console.log('PreferenceSettings: Starting config data fetch');
        
        // Fetch all config data in parallel
        const [
          jobRolesData,
          experienceLevelsData,
          languagesData,
          interviewGoalsData
        ] = await Promise.all([
          configService.getJobRoles(),
          configService.getExperienceLevels(),
          configService.getLanguages(),
          configService.getInterviewGoals()
        ]);
        
        console.log('PreferenceSettings: Received config data:', {
          jobRoles: jobRolesData,
          experienceLevels: experienceLevelsData,
          languages: languagesData,
          interviewGoals: interviewGoalsData
        });

        // Helper function to normalize config data
        const normalizeConfigData = (data: any, type: string) => {
          // If data is already an array
          if (Array.isArray(data)) {
            return data;
          }
          
          // If data has an item at index 0 and _response property (the format we're seeing in logs)
          if (data && typeof data === 'object' && '0' in data && '_response' in data) {
            console.log(`PreferenceSettings: Found special format for ${type}, extracting item at index 0`);
            return [data[0]];
          }
          
          // If data is a single object, wrap it in array
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            console.log(`PreferenceSettings: Found single object for ${type}, wrapping in array`);
            return [data];
          }
          
          // Default fallback
          console.warn(`PreferenceSettings: Unable to normalize ${type} data:`, data);
          return [];
        };
        
        // Extract and normalize data
        const extractedJobRoles = normalizeConfigData(jobRolesData, 'jobRoles');
        const extractedExperienceLevels = normalizeConfigData(experienceLevelsData, 'experienceLevels');
        const extractedLanguages = normalizeConfigData(languagesData, 'languages');
        const extractedInterviewGoals = normalizeConfigData(interviewGoalsData, 'interviewGoals');
            
        console.log('PreferenceSettings: Extracted job roles:', extractedJobRoles);
        console.log('PreferenceSettings: Extracted experience levels:', extractedExperienceLevels);
        console.log('PreferenceSettings: Extracted languages:', extractedLanguages);
        console.log('PreferenceSettings: Extracted interview goals:', extractedInterviewGoals);
        
        // Validate data before updating state
        const validJobRoles = Array.isArray(extractedJobRoles) && extractedJobRoles.length > 0 
          ? extractedJobRoles.filter(role => role && typeof role === 'object' && ('title' in role || 'name' in role))
          : [];
        
        const validExperienceLevels = Array.isArray(extractedExperienceLevels) && extractedExperienceLevels.length > 0
          ? extractedExperienceLevels.filter(level => level && typeof level === 'object' && 'name' in level)
          : [];
          
        const validLanguages = Array.isArray(extractedLanguages) && extractedLanguages.length > 0
          ? extractedLanguages.filter(lang => lang && typeof lang === 'object' && 'name' in lang)
          : [];
          
        const validInterviewGoals = Array.isArray(extractedInterviewGoals) && extractedInterviewGoals.length > 0
          ? extractedInterviewGoals.filter(goal => goal && typeof goal === 'object' && 'name' in goal)
          : [];
        
        console.log('PreferenceSettings: Validated config data:', {
          jobRoles: validJobRoles.length,
          experienceLevels: validExperienceLevels.length,
          languages: validLanguages.length,
          interviewGoals: validInterviewGoals.length
        });
        
        // Check if any of the arrays are empty, which might indicate a problem
        if (!validJobRoles.length && !validExperienceLevels.length && !validLanguages.length && !validInterviewGoals.length) {
          console.error('PreferenceSettings: All configuration data arrays are empty or invalid. This might indicate an API or data parsing issue.');
          throw new Error('Failed to load configuration data. Please try again later.');
        }
        
        // Update state with validated data
        setJobRoles(validJobRoles);
        
        // Sort experience levels by order field if available
        const sortedExperienceLevels = [...validExperienceLevels].sort((a, b) => 
          (a.order !== undefined && b.order !== undefined) ? a.order - b.order : 0
        );
        setExperienceLevels(sortedExperienceLevels);
        
        setLanguages(validLanguages);
        setInterviewGoals(validInterviewGoals);
        
        console.log('PreferenceSettings: Config data loaded successfully. State updated with:', {
          jobRoles: validJobRoles.map(r => r.title || r.name),
          experienceLevels: sortedExperienceLevels.map(e => e.name),
          languages: validLanguages.map(l => l.name),
          interviewGoals: validInterviewGoals.map(g => g.name)
        });
        
        // Fetch user preferences after config data is loaded
        await fetchCareerPreferences(validJobRoles, sortedExperienceLevels, validLanguages, validInterviewGoals, true);
      } catch (err) {
        console.error('PreferenceSettings: Failed to fetch configuration data:', err);
        setConfigError('There was an error loading the preferences. Please try again.');
      } finally {
        setIsLoadingConfig(false);
      }
    };
    
    fetchConfigData();
  }, []);

  // Define fetchCareerPreferences function outside of useEffect for reusability
  const fetchCareerPreferences = async (
    currentJobRoles = jobRoles,
    currentExperienceLevels = experienceLevels,
    currentLanguages = languages,
    currentInterviewGoals = interviewGoals,
    forceRefresh = false  // Add parameter with default false
  ) => {
    try {
      // Check if we have config data loaded first
      if (!currentJobRoles.length || !currentExperienceLevels.length || !currentLanguages.length || !currentInterviewGoals.length) {
        console.log('PreferenceSettings: Config data not yet loaded, waiting to fetch preferences');
        return;
      }
      
      console.log(`PreferenceSettings: Fetching career preferences (forceRefresh=${forceRefresh})`);
      const careerPreferences = await careerService.getPreferences(); // Only force refresh when needed
      
      if (!careerPreferences) {
        console.log('PreferenceSettings: No existing career preferences found');
        return;
      }
      
      console.log('PreferenceSettings: API response received:', careerPreferences);
      
      if (!careerPreferences._id) {
        console.error('PreferenceSettings: Invalid career preferences - missing _id');
        return;
      }
      
      // Debug the structure of the preferences data
      console.log('PreferenceSettings: Career preferences structure:', {
        id: careerPreferences._id,
        hasJobRoles: Array.isArray(careerPreferences.preferredJobRoles) ? careerPreferences.preferredJobRoles.length : 'not an array',
        hasExperienceLevel: careerPreferences.experienceLevel ? typeof careerPreferences.experienceLevel : 'none',
        hasLanguages: Array.isArray(careerPreferences.languages) ? careerPreferences.languages.length : 'not an array',
        hasInterviewGoals: Array.isArray(careerPreferences.interviewGoals) ? careerPreferences.interviewGoals.length : 'not an array'
      });
      
      if (Array.isArray(careerPreferences.preferredJobRoles) && careerPreferences.preferredJobRoles.length > 0) {
        console.log('PreferenceSettings: Sample job role:', careerPreferences.preferredJobRoles[0]);
      }
      
      // Convert API format to UI format
      const uiPreferences = careerService.convertToUiFormat(careerPreferences, {
        jobRoles: currentJobRoles,
        experienceLevels: currentExperienceLevels,
        languages: currentLanguages,
        interviewGoals: currentInterviewGoals,
      });
      console.log('PreferenceSettings: Career preferences converted to UI format:', uiPreferences);
      
      // Check if we have valid data before updating state
      if (!uiPreferences.jobRoles.length && !uiPreferences.languages.length && 
          !uiPreferences.interviewGoals.length && !uiPreferences.experienceLevel) {
        console.warn('PreferenceSettings: Converted preferences contain no data, trying manual extraction');
        
        // Let's try to extract the data manually as a fallback
        const fallbackPreferences: UserPreferences = {
          jobRoles: [],
          experienceLevel: '',
          languages: [],
          interviewGoals: []
        };
        
        // Manual extraction of job roles
        if (Array.isArray(careerPreferences.preferredJobRoles)) {
          fallbackPreferences.jobRoles = careerPreferences.preferredJobRoles
            .filter(role => typeof role === 'string')
            .map(role => role as string);
        }
        
        // Manual extraction of experience level
        if (careerPreferences.experienceLevel) {
          if (typeof careerPreferences.experienceLevel === 'string') {
            fallbackPreferences.experienceLevel = careerPreferences.experienceLevel;
          } else if (typeof careerPreferences.experienceLevel === 'object' && careerPreferences.experienceLevel) {
            fallbackPreferences.experienceLevel = careerPreferences.experienceLevel.name || '';
          }
        }
        
        // Manual extraction of languages
        if (Array.isArray(careerPreferences.languages)) {
          fallbackPreferences.languages = careerPreferences.languages
            .filter(lang => typeof lang === 'string')
            .map(lang => lang as string);
        }
        
        // Manual extraction of interview goals
        if (Array.isArray(careerPreferences.interviewGoals)) {
          fallbackPreferences.interviewGoals = careerPreferences.interviewGoals
            .filter(goal => typeof goal === 'string')
            .map(goal => goal as string);
        }
        
        console.log('PreferenceSettings: Fallback preferences extraction result:', fallbackPreferences);
        
        if (fallbackPreferences.jobRoles.length || fallbackPreferences.languages.length || 
            fallbackPreferences.interviewGoals.length || fallbackPreferences.experienceLevel) {
          console.log('PreferenceSettings: Fallback extraction successful, updating state');
          setPreferences(fallbackPreferences);
          return;
        } else {
          console.error('PreferenceSettings: Both conversion methods failed to extract data');
          return;
        }
      }

      // If we reach here, we have valid UI preferences data
      console.log('PreferenceSettings: Regular conversion successful, updating state');
      
      // Force re-render by creating a new object
      const newPreferences = {
        jobRoles: [...uiPreferences.jobRoles],
        experienceLevel: uiPreferences.experienceLevel,
        languages: [...uiPreferences.languages],
        interviewGoals: [...uiPreferences.interviewGoals]
      };
      
      console.log('PreferenceSettings: New preferences to be set:', {
        jobRoles: newPreferences.jobRoles,
        experienceLevel: newPreferences.experienceLevel,
        languages: newPreferences.languages,
        interviewGoals: newPreferences.interviewGoals
      });
      
      // Set the new preferences state
      setPreferences(newPreferences);
      
      console.log('PreferenceSettings: State updated successfully');
      
    } catch (error) {
      console.error('PreferenceSettings: Error fetching career preferences:', error);
      // Don't show an error message as this is a background fetch
      // If data couldn't be loaded, the user can still input new preferences
    }
  };

  // Fetch real resumes from API instead of using mock data
  useEffect(() => {
    const fetchResumes = async () => {
      setLoading(true);
      setError(undefined);
      
      try {
        // Use careerService to fetch resumes
        const resumeData = await careerService.getResumes();
        console.log('PreferenceSettings: Fetched resumes from API:', resumeData.length);
        setResumes(resumeData);
      } catch (err) {
        console.error('PreferenceSettings: Failed to fetch resumes:', err);
        setError('Failed to fetch resumes. Please try refreshing if resume is not found.');
      } finally {
        setLoading(false);
      }
    };

    fetchResumes();
  }, []);

  // Listen for resume upload events from other components
  useEffect(() => {
    const handleResumeUploaded = async () => {
      console.log('PreferenceSettings: Resume uploaded event received from another component');
      // Clear cache and refresh
      careerService.clearResumeCache();
      const resumeData = await careerService.getResumes(true);
      setResumes(resumeData);
    };

    window.addEventListener('resume-uploaded', handleResumeUploaded);
    
    return () => {
      window.removeEventListener('resume-uploaded', handleResumeUploaded);
    };
  }, []);

  const handleRefreshResumes = async () => {
    // Actually refresh resumes from the API instead of using mock data
    setLoading(true);
    setError(undefined);
    
    try {
      // Clear the cache first to ensure fresh data everywhere
      careerService.clearResumeCache();
      
      // Force refresh from the API by passing true
      const resumeData = await careerService.getResumes(true);
      console.log('PreferenceSettings: Refreshed resumes from API:', resumeData.length);
      setResumes(resumeData);

      if (resumeData.length > 0 && jobRoles.length > 0 && experienceLevels.length > 0 && languages.length > 0 && interviewGoals.length > 0) {
        localStorage.setItem('hasCompletedProfileSetup', 'true');
      } else {
        localStorage.setItem('hasCompletedProfileSetup', 'false');
      }
    } catch (err) {
      console.error('PreferenceSettings: Failed to refresh resumes:', err);
      setError('Failed to refresh resumes. Please try again.');
      showToast('Failed to refresh resumes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleJobRolesChange = (jobRoles: string[]) => {
    setPreferences({
      ...preferences,
      jobRoles
    });
  };

  const handleExperienceLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPreferences({
      ...preferences,
      experienceLevel: e.target.value
    });
  };

  const handleLanguagesChange = (languages: string[]) => {
    setPreferences({
      ...preferences,
      languages
    });
  };

  const handleInterviewGoalsChange = (interviewGoals: string[]) => {
    setPreferences({
      ...preferences,
      interviewGoals
    });
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      setShowValidationError(true);
      return;
    }
    
    setIsSaving(true);
    setError(undefined);
    
    try {
      // Convert UI preferences to API format
      const apiPreferences = careerService.convertToApiFormat(preferences, {
        jobRoles,
        experienceLevels,
        languages,
        interviewGoals
      });
      
      console.log('PreferenceSettings: Saving career preferences...');
      
        // Update existing preferences
        console.log('PreferenceSettings: Updating existing career preferences...');
        let result = await careerService.updatePreferences(apiPreferences);
      
      if (!result) {
        throw new Error('Failed to save preferences. Empty response from server.');
      }
      
      console.log('PreferenceSettings: Career preferences saved successfully:', result._id);
      
      // Refresh the preferences data from API to ensure UI is in sync
      await fetchCareerPreferences();
      
      // Show success message
      setIsSaved(true);
      showToast('Preferences saved successfully', 'success');
      
      // Dispatch custom event to notify other components that career preferences have changed
      // This will trigger recommendation refreshes in the PrepareForInterview component
      const careerPrefsChangedEvent = new CustomEvent('career-preferences-changed', {
        detail: { timestamp: Date.now() }
      });
      window.dispatchEvent(careerPrefsChangedEvent);
      console.log('Dispatched career-preferences-changed event to refresh recommendations');
      
      if (jobRoles.length > 0 && experienceLevels.length > 0 && languages.length > 0 && interviewGoals.length > 0 && resumes.length > 0) {
        localStorage.setItem('hasCompletedProfileSetup', 'true');
      } else {
        localStorage.setItem('hasCompletedProfileSetup', 'false');
      }
      
      // Reset the saved status after a few seconds
      setTimeout(() => {
        setIsSaved(false);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving preferences:', err);
      const errorMessage = err?.message || 'Failed to save preferences. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid = () => {
    return (
      preferences.jobRoles.length > 0 && 
      preferences.experienceLevel && 
      preferences.languages.length > 0 && 
      preferences.interviewGoals.length > 0
    );
  };

  const handleCreateResume = () => {
    setIsUploadModalOpen(true);
  };
  
  const handleManageResumes = () => {
    window.open('https://dashboard.jobprot.com/seeker/dashboard', '_blank');
  };

  const handleOpenResumePreview = (resume: ResumeProtocol) => {
    setSelectedResume(resume);
    setIsResumeModalOpen(true);
  };

  const handleUploadSuccess = () => {
    // Refresh resumes after successful upload
    handleRefreshResumes();
    
    // Dispatch a global event to notify other components
    const resumeUploadedEvent = new CustomEvent('resume-uploaded', {
      detail: { timestamp: Date.now() }
    });
    window.dispatchEvent(resumeUploadedEvent);
    console.log('Dispatched resume-uploaded event to notify other components');
  };

  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };
  
  // Error message for configuration loading failure
  if (configError) {
    const handleRetryWithoutReload = () => {
      // Clear the cache and refetch
      configService.clearCache();
      setConfigError(undefined);
      setIsLoadingConfig(true);
      
      // Fetch all config data in parallel
      Promise.all([
        configService.getJobRoles(),
        configService.getExperienceLevels(),
        configService.getLanguages(),
        configService.getInterviewGoals()
      ])
      .then(([jobRolesData, experienceLevelsData, languagesData, interviewGoalsData]) => {
        // Update state with fetched data
        setJobRoles(jobRolesData);
        
        // Sort experience levels by order field
        const sortedExperienceLevels = [...experienceLevelsData].sort((a, b) => a.order - b.order);
        setExperienceLevels(sortedExperienceLevels);
        
        setLanguages(languagesData);
        setInterviewGoals(interviewGoalsData);
        
        console.log('PreferenceSettings: Config data refreshed successfully');
      })
      .catch(err => {
        console.error('PreferenceSettings: Failed to refresh configuration data:', err);
        setConfigError('Failed to refresh configuration data. Please try reloading the page.');
      })
      .finally(() => {
        setIsLoadingConfig(false);
      });
    };
    
    return (
      <div className="space-y-8">
        {isLoadingConfig ? (
          <div className="bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark">
            <LoadingIndicator 
              size="medium" 
              text="Refreshing preferences..." 
              items={['Job Roles', 'Experience Levels', 'Languages', 'Interview Goals']}
              padding="large"
            />
          </div>
        ) : (
          <div className="text-center justify-center items-center bg-error-light/10 dark:bg-error-dark/10 border border-border-light dark:border-border-dark p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-3">Failed to load preferences</h2>
            <p className="mb-4 text-base text-text-secondary-light dark:text-text-secondary-dark">{configError}</p>
            <div className="flex justify-center space-x-4">
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">

          {/* Resume Section */}
          <div className="w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
            <div className="flex justify-between items-start mb-4">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
              <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">Your Resumes</h2>
              {resumes.length === 0 && <p className="p-2 px-3 bg-amber-700/10 dark:bg-amber-500/10 dark:text-amber-500 text-amber-700 text-xs rounded-full font-medium font-heading">Optional, but good to have</p>}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRefreshResumes}
                leftIcon={<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />}
                loading={loading}
              >
                 Refresh
              </Button>
            </div>

            {error && (
              <div className="text-center justify-center items-center bg-error-light/10 dark:bg-error-dark/10 text-error-light dark:text-error-dark p-4 rounded-lg mb-4">
                <p className="text-sm text-text-primary-light dark:text-text-primary-dark font-medium">{error}</p>
              </div>
            )}

            {!error && resumes.length === 0 ? (
              <div className="py-8 flex flex-col items-center text-center">
                {/* Hidden on mobile using responsive state */}
                {!isMobileView && (
                  <div className="bg-surface-light dark:bg-surface-dark rounded-full p-4 mb-4">
                    <FileText size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                )}
                <h3 className="text-lg font-heading font-semibold mb-2">You have no resumes</h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-md text-sm">
                  Resume will be used to personalize your preparation journey. Upload one or create one with JobProt.
                </p>
              <div className="flex flex-col md:flex-row w-full md:w-auto justify-center gap-3">
              <Button
                  variant="primary"
                  onClick={handleCreateResume}
                  leftIcon={<Upload size={18} />}
                  className="w-full md:w-auto"
                >
                  Upload Resume
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.open('https://dashboard.jobprot.com/resume-upload?action=create', '_blank')}
                  loading={loading}
                  rightIcon={<ExternalLink size={18} />}
                  className="w-full md:w-auto"
                >
                  Create With JobProt
                </Button>

                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4 -mt-2">
                    The following resume will be used to personalize your mock interviews
                  </p>
                  
                  {resumes.sort((a, b) => new Date(b.updatedAt || '').getTime() - new Date(a.updatedAt || '').getTime()).map(resume => (
                    <div 
                      key={resume._id}
                      className="mb-4 p-3 md:p-4 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300 cursor-pointer"
                      onClick={() => handleOpenResumePreview(resume)}
                    >
                      <div className="flex items-start">
                        {!isMobileView && (
                          <div className="mr-4 bg-primary-light/10 dark:bg-primary-dark/10 p-2.5 rounded-full">
                            <FileText size={22} className="text-primary-light dark:text-primary-dark" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-base text-text-primary-light dark:text-text-primary-dark mb-1">
                            {resume.personalDetails.name.split(' ')[0]} - {resume._id?.slice(-12)}
                          </h3>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                            {(() => {
                              if (resume.workExperience.length > 0) {
                                // First check if there's a current job
                                const currentJob = resume.workExperience.find(job => job.isCurrentJob);
                                
                                if (currentJob && currentJob.jobTitle && currentJob.companyName) {
                                  // Show current job
                                  return `${currentJob.jobTitle} at ${currentJob.companyName}`;
                                } else if (resume.workExperience[0]?.jobTitle && resume.workExperience[0]?.companyName) {
                                  // Show first job experience if no current job
                                  return `Worked at ${resume.workExperience[0].companyName} as ${resume.workExperience[0].jobTitle}`;
                                }
                              }
                              return 'No work experience added';
                            })()}
                          </p>
                          {isMobileView && (
                          <div className="mt-2 text-text-tertiary-light dark:text-text-tertiary-dark text-xs">
                            <span>Updated: {formatDate(resume.updatedAt || '')}</span>
                          </div>
                        )}
                        </div>
                        {!isMobileView && (
                          <div className="flex items-center text-xs text-text-tertiary-light dark:text-text-tertiary-dark rounded-2xl bg-background-light/60 dark:bg-background-dark/20 px-4 py-2">
                            <span>{!isMobileView ? 'Last updated: ' : 'Updated:'} {formatDate(resume.updatedAt || '')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={handleManageResumes}
                    rightIcon={<ExternalLink size={16} />}
                    className="w-full md:w-auto"
                  >
                    Manage Resumes
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Career Preferences Section */}
          <div className="w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
            <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Career Preferences
            </h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
              Tell us about your career goals and preferences so we can tailor your mock interview experience.
            </p>

            {isLoadingConfig ? (
              <div className="bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark">
                <LoadingIndicator 
                  size="medium" 
                  text="Loading configurations..." 
                  items={['Job Roles', 'Experience Levels', 'Languages', 'Interview Goals']}
                  padding="large"
                />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Job Roles */}
                <div>
                  <div className="flex items-center mb-2">
                    <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Enter Job Roles</h3>
                  </div>
                  <div className="mt-2">
                    <FreeTextChipField
                      value={preferences.jobRoles}
                      suggestions={jobRoles.length > 0 
                        ? jobRoles.map(role => role.title || role.name).filter(Boolean) as string[]
                        : []
                      }
                      onChange={handleJobRolesChange}
                      placeholder="Type job roles and press Enter or comma to add (e.g., Software Engineer, Product Manager)"
                      maxItems={5}
                      helperText="Select upto 5 job roles, You can also paste comma-separated roles."
                    />
                  </div>
                </div>

                {/* Experience Level */}
                <div>
                  <div className="flex items-center mb-2">
                    <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Your Experience Level</h3>
                  </div>
                  <div className="mt-2 mb-2">
                  <select
                    value={preferences.experienceLevel}
                    onChange={handleExperienceLevelChange}
                    className="w-full flex font-medium flex-wrap p-3 bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark text-sm border border-border-light dark:border-border-dark rounded-lg focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all min-h-[56px] appearance-none"
                  >
                    <option value="" disabled>
                      {experienceLevels.length > 0 ? "Select experience level..." : "Loading experience levels..."}
                    </option>
                    {experienceLevels.length > 0 && experienceLevels.map((level) => (
                      <option key={level._id} value={level.name}>{level.name} - {level.description}</option>
                    ))}
                  </select>
                  </div>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    Select your experience level, this will help us tailor the interview questions to your experience level
                  </p>
                </div>

                {/* Languages */}
                <div>
                  <div className="flex items-center mb-2">
                    <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Select Languages</h3>
                  </div>
                  <div className="mt-2">
                    <AutocompleteChipField
                      value={preferences.languages}
                      options={languages.length > 0 ? languages.map(lang => lang.name).filter(Boolean) as string[] : []}
                      onChange={handleLanguagesChange}
                      placeholder={languages.length > 0 ? "Add languages..." : "Loading languages..."}
                      maxItems={5}
                      helperText="Select the languages you're comfortable with"
                    />
                  </div>
                </div>

                {/* Interview Goals */}
                <div>
                  <div className="flex items-center mb-2">
                    <h3 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">Your Interview Goals</h3>
                  </div>
                  <div className="mt-2">
                    <AutocompleteChipField
                      value={preferences.interviewGoals}
                      options={interviewGoals.length > 0 ? interviewGoals.map(goal => goal.name).filter(Boolean) as string[] : []}
                      onChange={handleInterviewGoalsChange}
                      placeholder={interviewGoals.length > 0 ? "Add interview goals..." : "Loading interview goals..."}
                      maxItems={3}
                      helperText="Select up to 3 interview goals. We'll use these to setup your interview preparation accordingly."
                    />
                  </div>
                </div>

                {/* Validation Error Message */}
                {showValidationError && (
                  <div className="mt-4">
                    <p className="text-error-light dark:text-error-dark text-sm font-medium">
                      Please complete all required fields to save your preferences.
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <Button
                variant="primary"
                onClick={handleSave}
                loading={isSaving || isLoadingConfig}
                disabled={!isFormValid() || isSaving || isLoadingConfig}
                leftIcon={isSaving ? null : <Save size={18} />}
              >
                {isSaving ? 'Saving...' : isSaved ? 'Saved!' : 'Save Preferences'}
              </Button>
            </div>
          </div>

      {/* Resume Preview Modal */}
      {selectedResume && (
        <ResumePreviewModal
          isOpen={isResumeModalOpen}
          onClose={() => setIsResumeModalOpen(false)}
          resume={selectedResume}
        />
      )}
      
      {/* Resume Upload Modal */}
      <ResumeUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
}; 